import React, { useState } from 'react';
import { ShieldCheckIcon, BriefcaseIcon, CheckCircleIcon, ChevronDownIcon, BookOpenIcon } from 'lucide-react';
import { SchoolListings } from '../components/SchoolListings';
import { useSchoolSearch } from '../hooks/useSchoolSearch';
import { useGeoLocation } from '../hooks/useGeoLocation';
import { useMetaTags } from '../hooks/useMetaTags';
import { programContent, getProgramContent } from '../data/programContent';
import { areaOfStudyData } from '../data/areaOfStudyData';
import { ContentSection } from '../components/ContentSection';
interface ProgramLandingPageProps {
  programId: number;
  degreeLevel: number;
  onProgramChange: (programId: number) => void;
}
export function ProgramLandingPage({
  programId,
  degreeLevel,
  onProgramChange
}: ProgramLandingPageProps) {
  const programData = getProgramContent(programId);
  const areaData = programData ? areaOfStudyData[programData.areaId] : areaOfStudyData[1];
  const {
    geo
  } = useGeoLocation();
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest: degreeLevel,
    mProgram: programId,
    zipCode: geo.zipCode
  });
  // Use program-specific content if available, otherwise fall back to area content
  const headline = programData?.headline || areaData.headline;
  const description = programData?.description || areaData.description;
  useMetaTags({
    title: programData ? `${programData.name} Degree Programs | DegreeCompare` : `Online Degree Programs | DegreeCompare`,
    description: description,
    keywords: programData ? `online ${programData.name.toLowerCase()} degree, ${programData.name.toLowerCase()} programs, accredited ${programData.name.toLowerCase()} colleges` : 'online degree programs, accredited colleges',
    ogTitle: headline,
    ogDescription: description
  });
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().toLocaleString('default', {
    month: 'short'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-1628" data-mp="2vviorqv9bovz">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-8 md:py-12 px-4 relative overflow-hidden" data-id="element-1629" data-mp="wn4nepzy7dmd">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1630" data-mp="rp372ctwpsel"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-1631" data-mp="10qj4zl3jc21x">
          <div className="max-w-3xl" data-id="element-1632" data-mp="10r2jertz88b7">
            <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-gray-200 mb-3 border border-white/20" data-id="element-1633" data-mp="d0f9k10i38m0">
              <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-1634" data-mp="8xabcangdt36" />
              <span data-id="element-1635" data-mp="mra03hks4ud9">
                Last Updated: {currentMonth} {currentYear}
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold leading-tight mb-2" data-id="element-1636" data-mp="hygt0n1u99y0">
              {headline}
            </h1>

            <p className="text-sm md:text-base text-gray-300 max-w-xl" data-id="element-1637" data-mp="3lgiazp10s11w">
              {description}
            </p>
          </div>
        </div>
      </section>

      {/* School Listings */}
      <SchoolListings schools={schools} loading={loading} error={error} selectedAreaId={programData?.areaId || 1} selectedProgram={programId} onProgramChange={onProgramChange} data-id="element-1638" data-mp="20wmxa6t7ms2c" />

      {/* Program-Specific Content */}
      {programData && (programData.sections || programData.jobTitles || programData.faq) ? <section className="w-full bg-white border-t border-gray-200 py-16" data-id="element-1639" data-mp="2kmgskuv4vpbb">
          <div className="max-w-4xl mx-auto px-4" data-id="element-1640" data-mp="2kkce5izlz7ws">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-1641" data-mp="2oobuo1kw26q5">
              Is a Degree in {programData.name} Right for You?
            </h2>

            <div className="prose prose-lg max-w-none text-gray-700" data-id="element-1642" data-mp="2aqn54gfvmber">
              <p className="text-xl leading-relaxed mb-10 text-gray-600 text-center" data-id="element-1643" data-mp="2flsujp8hm87l">
                {programData.whyStudy}
              </p>

              {/* Career Outlook */}
              <div className="mb-12" data-id="element-1644" data-mp="14902edbsbhr0">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100" data-id="element-1645" data-mp="145ucivvc0bu8">
                  <div className="flex items-center gap-3 mb-4" data-id="element-1646" data-mp="1jj55trq0jyzp">
                    <div className="bg-blue-100 p-2 rounded-lg text-blue-600" data-id="element-1647" data-mp="1eq2ja3jnlpt0">
                      <BriefcaseIcon className="w-6 h-6" data-id="element-1648" data-mp="1ap0hbeycqznc" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 m-0" data-id="element-1649" data-mp="15qfdz6nszzuo">
                      Career Outlook
                    </h3>
                  </div>
                  <p className="text-sm m-0" data-id="element-1650" data-mp="22g6cri34o96a">{programData.careerOutlook}</p>
                </div>
              </div>

              {/* Job Titles */}
              {programData.jobTitles && programData.jobTitles.length > 0 && <div className="mb-12" data-id="element-1651" data-mp="1ti4f9cilrynr">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1652" data-mp="1ohgcxuoxbve8">
                    Common Career Paths
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1653" data-mp="i1nynfvbbbzb">
                    {programData.jobTitles.map((title, idx) => <div key={idx} className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-1654" data-mp="d0gq3elci8rl" data-mpi={idx}>
                        <div className="w-2 h-2 rounded-full bg-[#3b82f6]" data-id="element-1655" data-mp="7wxpaoxkx5l2"></div>
                        <span className="font-medium text-gray-900" data-id="element-1656" data-mp="7utoifuz8mp0">
                          {title}
                        </span>
                      </div>)}
                  </div>
                </div>}

              {/* Program Sections */}
              {programData.sections && programData.sections.length > 0 && <div className="space-y-8 mb-12" data-id="element-1657" data-mp="lqp000n1qvid">
                  {programData.sections.map((section, idx) => <div key={idx} data-id="element-1658" data-mp="qpie9va5qqjl" data-mpi={idx}>
                      <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1659" data-mp="vlz3ytx09cys">
                        {section.heading}
                      </h3>
                      <p className="text-base text-gray-700 leading-relaxed" data-id="element-1660" data-mp="10ktvck91g9ui">
                        {section.body}
                      </p>
                    </div>)}
                </div>}

              {/* What to Look For */}
              {programData.whatToLookFor && <div className="bg-gray-900 text-white rounded-2xl p-8 md:p-10 shadow-xl mb-12" data-id="element-1661" data-mp="2ubqxw01kwqpa">
                  <h3 className="text-2xl font-bold text-white mb-4 mt-0" data-id="element-1662" data-mp="2pdpfzifw2xfe">
                    What to Look For in a Program
                  </h3>
                  <div className="flex items-start gap-4" data-id="element-1663" data-mp="34brjjsmx7iwn">
                    <CheckCircleIcon className="w-8 h-8 text-[#22c55e] flex-shrink-0 mt-1" data-id="element-1664" data-mp="2zb3q70b7ypz9" />
                    <p className="text-gray-300 text-lg leading-relaxed m-0" data-id="element-1665" data-mp="3dacxtbld0brm">
                      {programData.whatToLookFor}
                    </p>
                  </div>
                </div>}

              {/* FAQ Section */}
              {programData.faq && programData.faq.length > 0 && <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8" data-id="element-1666" data-mp="3i24qbro2srke">
                  <div className="flex items-center gap-2 mb-5" data-id="element-1667" data-mp="3w1m7plz1l2jq">
                    <BookOpenIcon className="w-5 h-5 text-[#e8594f]" data-id="element-1668" data-mp="3w1vyvcvtpfr0" />
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-1669" data-mp="3s12bqlq7g4z6">
                      Frequently Asked Questions
                    </h3>
                  </div>

                  <div className="space-y-1" data-id="element-1670" data-mp="jhrws9rddrsc">
                    {programData.faq.map((item, idx) => <FaqItem key={idx} question={item.question} answer={item.answer} defaultOpen={idx === 0} data-id="element-1671" data-mp="jfntnu1aowt4" data-mpi={idx} />)}
                  </div>
                </div>}
            </div>
          </div>
        </section> : <ContentSection areaData={areaData} data-id="element-1672" data-mp="aj6jou4o34yy" />}
    </div>;
}
function FaqItem({
  question,
  answer,
  defaultOpen
}: {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen || false);
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-1673" data-mp="3iy6nzsh9syqh">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between py-3 text-left hover:text-[#3b82f6] transition-colors" data-id="element-1674" data-mp="3myh4w06lp9k4">
        <span className="text-sm font-semibold text-gray-900 pr-4" data-id="element-1675" data-mp="3912asi5ekdlt">
          {question}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1676" data-mp="3dzwsfgrmvw4c" />
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-40 opacity-100 pb-3' : 'max-h-0 opacity-0'}`} data-id="element-1677" data-mp="3oi20lg0qm8ms">
        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1678" data-mp="3khi3gcg5z50n">{answer}</p>
      </div>
    </div>;
}